import { Device, Trip } from "@fairfleet/geotab";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { AuditDevice } from "../models/AuditDevice";
import { JOURNEY_MAX_MS } from "../utils/analogJourneys";
import { ANNOTATION_MAX_LEAD_MS } from "../utils/annotationForTrip";
import { createAuditEntriesBuilder } from "../utils/buildAuditEntries";
import { resolveFuelType } from "../utils/groupHierarchy";
import { ReasonTaxClassMap } from "../utils/reasonTaxClass";
import { endOfCalendarDayInZone, parseDayKey, startOfCalendarDayInZone } from "../utils/timeZone";
import { DayConsumptionLookup, electricEnergyForWindow, fuelForWindow } from "../utils/tripConsumption";
import { classifyVehicleCheck, readCustomProperty } from "../utils/vehicleCheck";
import { AUDIT_DEVICES_QUERY_KEY } from "./useAuditDevices";
import { useGeotabClient } from "@/components/GeotabClientContext";
import {
  VEHICLE_CHECK_LAST_CHECK_PROPERTY_NAME,
  VEHICLE_CHECK_PROPERTY_NAMES,
} from "@/constants/VehicleCheckWords";
import {
  DUTY_ROSTERS_QUERY_KEY,
  DutyRosterRecord,
  TripEndRecord,
  useManualLogbookRecords,
} from "@/hooks/addins/useManualLogbookDutyRosters";
import { CHARGE_EVENTS_QUERY_KEY, useChargeEventsForDevices } from "@/hooks/chargeEvent/useChargeEventsForDevices";
import { FILL_UPS_QUERY_KEY, useFillUpsForDevices } from "@/hooks/fuel/useFillUpsForDevices";
import { FUEL_TRANSACTIONS_QUERY_KEY, useFuelTransactionsForDevices } from "@/hooks/fuel/useFuelTransactionsForDevices";
import { FUEL_USED_QUERY_KEY, useFuelUsedForDevices } from "@/hooks/fuel/useFuelUsedForDevices";
import { useProperties } from "@/hooks/properties/useProperties";
import { ELECTRIC_ENERGY_QUERY_KEY, useElectricEnergyForDevices } from "@/hooks/statusData/useElectricEnergyForDevices";
import { AUDIT_TRIPS_QUERY_KEY, useTripsForDevices } from "@/hooks/trips/useTripsForDevices";
import { GroupIndex } from "@/utilities/groupIndex";

/** Data sources of the audit, named in the load-error message. */
export type AuditSource =
  | "trips"
  | "bookings"
  | "fuelUsed"
  | "fuelTransactions"
  | "electricEnergy"
  | "fillUps"
  | "chargeEvents"
  | "properties";

interface Args {
  devices: Device[];
  fromDate: Date;
  toDate: Date;
  taxClasses: ReasonTaxClassMap;
  groupIndex: GroupIndex;
  /** Zone in which calendar days are cut (the Geotab user's zone). */
  timeZone: string;
}

/** `licensePlate` and the VIN live on the GoDevice subtype, not on the base `Device` type. */
type VehicleDevice = Device & { licensePlate?: string; vehicleIdentificationNumber?: string };

/**
 * Trips from this long before the period are loaded as well, so the first audited trip of a
 * vehicle gets its odometer start from the trip before it (a week covers weekends and holidays).
 */
const ODOMETER_LEAD_MS = 7 * 24 * 60 * 60 * 1000;

const EMPTY_TRIPS: Trip[] = [];
const EMPTY_ROSTERS: DutyRosterRecord[] = [];
const EMPTY_TRIP_ENDS: TripEndRecord[] = [];

/**
 * Loads everything the page needs for the selected vehicles and period and turns it into
 * logbook entries (`AuditTrip`): analog journeys from the drivers' duty roster records, digital
 * trips from Geotab trips with a driver logged in. Trips nobody booked become digital entries
 * without driver and reason; the period summary always counts their kilometres as
 * "unassigned", the table lists them only on request. Both views share this data; the split
 * happens in `useAuditRows`.
 */
export function useAuditData({ devices, fromDate, toDate, taxClasses, groupIndex, timeZone }: Args) {
  const queryClient = useQueryClient();
  const { activeSessionId } = useGeotabClient();
  const { data: propertiesById = {}, isError: propertiesFailed } = useProperties();

  const auditDevices = useMemo(() => {
    const map = new Map<string, AuditDevice>();
    for (const device of devices as VehicleDevice[]) {
      map.set(device.id, {
        id: device.id,
        name: device.name ?? "",
        licensePlate: device.licensePlate ?? "",
        vin: device.vehicleIdentificationNumber ?? "",
        fuelType: resolveFuelType(device, groupIndex),
        vehicleCheck: classifyVehicleCheck(
          readCustomProperty(device, VEHICLE_CHECK_PROPERTY_NAMES, propertiesById),
        ),
        lastCheck: readCustomProperty(device, [VEHICLE_CHECK_LAST_CHECK_PROPERTY_NAME], propertiesById),
      });
    }
    return map;
  }, [devices, groupIndex, propertiesById]);

  const deviceIds = useMemo(() => devices.map(d => d.id), [devices]);
  const electricIds = useMemo(
    () => deviceIds.filter(id => auditDevices.get(id)?.fuelType === "electric"),
    [deviceIds, auditDevices],
  );
  const combustionIds = useMemo(
    () =>
      deviceIds.filter(id => {
        const fuelType = auditDevices.get(id)?.fuelType;
        return fuelType !== undefined && fuelType !== "electric";
      }),
    [deviceIds, auditDevices],
  );
  // Fuel transactions exist for vehicles without a drive type group too (the group only sets
  // the unit); only electric vehicles have none.
  const nonElectricIds = useMemo(
    () => deviceIds.filter(id => auditDevices.get(id)?.fuelType !== "electric"),
    [deviceIds, auditDevices],
  );

  const trips = useTripsForDevices({
    deviceIds,
    fromDate,
    toDate,
    annotationLeadMs: ANNOTATION_MAX_LEAD_MS,
    tripLeadMs: ODOMETER_LEAD_MS,
  });

  // A journey confirmed up to a day before the period may still cover its first trips, and the
  // end entry of the period's last journey may come up to a day after it.
  const { data: manualRecords, isFetching: manualRecordsFetching, isError: manualRecordsFailed, error: manualRecordsError } = useManualLogbookRecords(fromDate, toDate, JOURNEY_MAX_MS, JOURNEY_MAX_MS);
  const dutyRosters = manualRecords?.dutyRosters ?? EMPTY_ROSTERS;
  const tripEnds = manualRecords?.tripEnds ?? EMPTY_TRIP_ENDS;
  const fuelUsed = useFuelUsedForDevices(combustionIds, fromDate, toDate);
  const fuelTransactions = useFuelTransactionsForDevices(nonElectricIds, fromDate, toDate);
  const electricEnergy = useElectricEnergyForDevices(electricIds, fromDate, toDate);
  const fillUps = useFillUpsForDevices(nonElectricIds, fromDate, toDate);
  const chargeEvents = useChargeEventsForDevices(electricIds, fromDate, toDate);

  /**
   * Whole-day consumption over the calendar day in the audit's zone, the figure Geotab's fuel
   * and EV report shows: energy out minus energy in for electric vehicles (including consumption
   * while parked), the FuelUsed events or counter delta of the day for combustion engines. Only
   * the period summary uses it; the day rows of the table sum their entries instead.
   */
  const dayConsumption = useCallback<DayConsumptionLookup>(
    (deviceId, dateKey) => {
      const fuelType = auditDevices.get(deviceId)?.fuelType;
      if (!fuelType) return undefined;
      const { year, month, day } = parseDayKey(dateKey);
      const window = {
        start: startOfCalendarDayInZone(year, month, day, timeZone),
        stop: endOfCalendarDayInZone(year, month, day, timeZone),
      };
      if (fuelType === "electric") return electricEnergyForWindow(window, electricEnergy.byDevice[deviceId]);
      return fuelForWindow(window, fuelUsed.byDevice[deviceId]);
    },
    [electricEnergy.byDevice, fuelUsed.byDevice, auditDevices, timeZone],
  );

  // Incremental: only the vehicle whose data just arrived is computed again.
  const buildEntries = useMemo(createAuditEntriesBuilder, []);
  const { auditTrips, vehicleDays, periodRefuels, periodDetected } = useMemo(
    () =>
      buildEntries({
        fromDate,
        toDate,
        timeZone,
        auditDevices,
        taxClasses,
        tripsByDevice: trips.byDevice,
        dutyRosters,
        tripEnds,
        fuelUsedByDevice: fuelUsed.byDevice,
        fuelTransactionsByDevice: fuelTransactions.byDevice,
        electricEnergyByDevice: electricEnergy.byDevice,
        fillUpsByDevice: fillUps.byDevice,
        chargeEventsByDevice: chargeEvents.byDevice,
      }),
    [buildEntries, trips.byDevice, auditDevices, dutyRosters, tripEnds, taxClasses, fuelUsed.byDevice, electricEnergy.byDevice, fuelTransactions.byDevice, fillUps.byDevice, chargeEvents.byDevice, fromDate, toDate, timeZone],
  );

  const failedSources = useMemo(
    () =>
      (
        [
          ["trips", trips.isError],
          ["bookings", manualRecordsFailed],
          ["fuelUsed", fuelUsed.isError],
          ["fuelTransactions", fuelTransactions.isError],
          ["electricEnergy", electricEnergy.isError],
          ["fillUps", fillUps.isError],
          ["chargeEvents", chargeEvents.isError],
          ["properties", propertiesFailed],
        ] as [AuditSource, boolean][]
      )
        .filter(([, failed]) => failed)
        .map(([source]) => source),
    [trips.isError, manualRecordsFailed, fuelUsed.isError, fuelTransactions.isError, electricEnergy.isError, fillUps.isError, chargeEvents.isError, propertiesFailed],
  );

  const refresh = useCallback(() => {
    for (const key of [
      AUDIT_DEVICES_QUERY_KEY,
      AUDIT_TRIPS_QUERY_KEY,
      DUTY_ROSTERS_QUERY_KEY,
      FUEL_USED_QUERY_KEY,
      FUEL_TRANSACTIONS_QUERY_KEY,
      ELECTRIC_ENERGY_QUERY_KEY,
      FILL_UPS_QUERY_KEY,
      CHARGE_EVENTS_QUERY_KEY,
    ]) {
      queryClient.invalidateQueries({ queryKey: [key, activeSessionId] });
    }
  }, [queryClient, activeSessionId]);

  // Every Geotab trip loaded, including the lead week before the period: the predecessors that
  // give a vehicle's first trip its start address.
  // Collected once the trips are in: while they arrive nothing reads it.
  const geotabTrips = useMemo(
    () => (trips.isFetching ? EMPTY_TRIPS : Object.values(trips.byDevice).flatMap(deviceTrips => deviceTrips.trips)),
    [trips.isFetching, trips.byDevice],
  );

  return {
    trips: auditTrips,
    geotabTrips,
    auditDevices,
    /** Kilometres of every Geotab trip per vehicle and calendar day of the period, booked or not. */
    vehicleDays,
    /** Every fuel transaction of the period per vehicle (app and import, fuel and AdBlue). */
    periodRefuels,
    /** Geotab's detected fill-ups and charging sessions of the period per vehicle. */
    periodDetected,
    dayConsumption,
    loadedCount: trips.loadedCount,
    totalCount: trips.totalCount,
    /** Geotab trips are still coming in; the rows cannot be built yet. */
    isLoadingTrips: trips.isFetching,
    /** Consumption, fuel, bookings: the rows may already stand, their figures still fill in. */
    isLoadingDetails:
      manualRecordsFetching ||
      fuelUsed.isFetching ||
      fuelTransactions.isFetching ||
      electricEnergy.isFetching ||
      fillUps.isFetching ||
      chargeEvents.isFetching,
    isFetching:
      trips.isFetching ||
      manualRecordsFetching ||
      fuelUsed.isFetching ||
      fuelTransactions.isFetching ||
      electricEnergy.isFetching ||
      fillUps.isFetching ||
      chargeEvents.isFetching,
    /** Sources that failed to load; their figures are missing, not zero. */
    failedSources,
    /** Why the bookings failed, when that is something the user can act on (a too long look-back). */
    bookingsError: manualRecordsError,
    isError: failedSources.length > 0,
    dataUpdatedAt: trips.dataUpdatedAt,
    refresh,
  };
}
